import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { EngravingLogo } from '@/components/EngravingLogo/EngravingLogo';
import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import { Price } from '@/components/Price';
import { CustomErrors } from '@/components/ServerErrors/ServerErrors';
import { Visibility } from '@/components/Visibility';
import type { EngravingDetails } from '@/lib/engraving';
import { getTranslatedProductName } from '@/lib/product';
import { commonMessages } from '@/messages/common';
import { engravingMessages } from '@/messages/engraving';

import type { EngravingCardProductVariant } from './EngravingCard.types';
import { isEngravingCardCheckoutProductVariant } from './EngravingCard.types';

import * as S from './EngravingCard.styles';

export type EngravingCardSize = 'small' | 'large';

export interface EngravingCardProps {
  cardSize?: EngravingCardSize;
  children?: ReactNode;
  customErrorMessage?: string;
  displayButtonAsIcon?: boolean;
  engravingDetails: EngravingDetails;
  hasBraceletSizeNotSelectedError: boolean;
  isDisabled?: boolean;
  isMyOrder?: boolean;
  isProcessing?: boolean;
  isRelatedProduct?: boolean;
  lineId: string;
  onDelete?: (lineId: string) => void;
  productVariant: EngravingCardProductVariant;
}

export type {
  EngravingCardCheckoutProductVariant,
  EngravingCardProductVariant,
} from './EngravingCard.types';
export { isEngravingCardCheckoutProductVariant } from './EngravingCard.types';

export const EngravingCard = ({
  cardSize = 'large',
  children,
  customErrorMessage,
  displayButtonAsIcon = false,
  engravingDetails,
  hasBraceletSizeNotSelectedError,
  isDisabled = false,
  isMyOrder = false,
  isProcessing = false,
  isRelatedProduct = false,
  lineId,
  onDelete,
  productVariant,
}: EngravingCardProps) => {
  const wrapperRef = useRef<null | HTMLElement>(null);

  const { formatMessage } = useIntl();
  const { product } = productVariant;

  const { productType } = product;

  useEffect(() => {
    if (hasBraceletSizeNotSelectedError && wrapperRef.current) {
      wrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasBraceletSizeNotSelectedError]);

  const { size, style, text } = engravingDetails;
  const logoWidth = isMyOrder ? 80 : cardSize === 'small' ? 48 : 96;

  return (
    <>
      <S.Wrapper
        ref={wrapperRef}
        $isRelatedProduct={isRelatedProduct}
        $productType={productType.name.toLowerCase()}
        data-testid={`EngravingCardWrapper-${lineId}`}
      >
        <S.CardAndButtonsWrapper>
          <S.Card data-testid={`EngravingCardCard-${lineId}`}>
            <Flex
              flexDirection='row'
              gridGap={isMyOrder ? 's200' : 's100'}
              justifyContent='flex-start'
            >
              <EngravingLogo
                height={cardSize === 'small' ? 49 : 98}
                width={logoWidth}
              />
              <Flex flexDirection='row' width='100%'>
                <S.MiddleWrapper
                  data-testid={`EngravingCardMiddleWrapper-${lineId}`}
                >
                  <div>
                    <S.Name
                      $size={cardSize}
                      data-testid={`EngravingCardProductName-${lineId}`}
                    >
                      {formatMessage(engravingMessages.engravingCardTitle)}
                    </S.Name>
                  </div>
                  <div>
                    {isProcessing ? (
                      <Loader
                        data-testid={`removeProductLoader-${lineId}`}
                        height={35}
                        noMargin
                        size='sm'
                      />
                    ) : (
                      <S.EngravingInfo $size={cardSize}>
                        <S.EngravingData $size={cardSize} $style={style}>
                          {formatMessage(engravingMessages.characters)}:{' '}
                          <p>{text}</p>
                        </S.EngravingData>
                        <Flex gridGap='s100'>
                          <S.EngravingData $size={cardSize} $style={style}>
                            {formatMessage(engravingMessages.style)}:{' '}
                            <p>{style}</p>
                          </S.EngravingData>
                          <S.EngravingData $size={cardSize} $style={style}>
                            {formatMessage(engravingMessages.size)}:{' '}
                            <p>{size}</p>
                          </S.EngravingData>
                        </Flex>
                      </S.EngravingInfo>
                    )}
                  </div>
                  {isMyOrder ? children : null}
                </S.MiddleWrapper>

                <S.EndWrapper>
                  {onDelete &&
                    isEngravingCardCheckoutProductVariant(productVariant) && (
                      <Visibility
                        handleDisplay={isProcessing}
                        xsHidden={isDisabled}
                      >
                        <S.ButtonsWrapper>
                          <S.DeleteMobile
                            $displayButtonAsIcon={displayButtonAsIcon}
                            aria-label={formatMessage(
                              commonMessages.removeEngravingFromProduct,
                              { name: getTranslatedProductName(product) },
                            )}
                            data-testid={`EngravingCardDeleteMobile-${lineId}`}
                            onClick={() =>
                              onDelete(productVariant.checkoutLineId)
                            }
                            type='button'
                          >
                            <S.DeleteMobileIcon aria-hidden='true' />
                          </S.DeleteMobile>
                          {!displayButtonAsIcon && (
                            <S.DeleteDesktop
                              ariaLabel={formatMessage(
                                commonMessages.removeEngravingFromProduct,
                                { name: getTranslatedProductName(product) },
                              )}
                              data-testid={`EngravingCardDeleteDesktop-${lineId}`}
                              label={formatMessage(commonMessages.remove)}
                              onClick={() =>
                                onDelete(productVariant.checkoutLineId)
                              }
                              variant='link'
                            />
                          )}
                        </S.ButtonsWrapper>
                      </Visibility>
                    )}
                  <Price
                    data-testid={`EngravingCardPrice-${lineId}`}
                    displayFreeIfUndefined={true}
                    displayZero={false}
                  />
                </S.EndWrapper>
              </Flex>
            </Flex>

            {isMyOrder ? null : children}
          </S.Card>
        </S.CardAndButtonsWrapper>
      </S.Wrapper>
      {customErrorMessage && (
        <CustomErrors message={customErrorMessage}></CustomErrors>
      )}
    </>
  );
};
